import { loadCommunityData, addCommunitysolve, incrementCommunityStarts } from "./firebase";
import { useState, useEffect, useRef, useCallback } from "react";

/*
 * ══════════════════════════════════════════════════════
 *  CLEVERLAND PUZZLE DESIGN — Daily Abstraction
 * ══════════════════════════════════════════════════════
 */

// ═══════════════════════════════════════
//  UTILITIES
// ═══════════════════════════════════════

function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function generatePuzzle(rows, cols, seed) {
  const rng = mulberry32(seed);
  const fillRate = 0.38 + rng() * 0.14;
  let grid = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => (rng() < fillRate ? 1 : 0))
  );
  const smooth = grid.map(r => [...r]);
  for (let i = 0; i < rows; i++) for (let j = 0; j < cols; j++) {
    let nb = 0, tot = 0;
    for (let di = -1; di <= 1; di++) for (let dj = -1; dj <= 1; dj++) {
      if (!di && !dj) continue;
      const ni = i + di, nj = j + dj;
      if (ni >= 0 && ni < rows && nj >= 0 && nj < cols) { tot++; if (grid[ni][nj]) nb++; }
    }
    if (nb >= tot * 0.6) smooth[i][j] = 1;
    else if (nb <= tot * 0.2) smooth[i][j] = 0;
  }
  grid = smooth;
  if (!grid[0].some(c => c === 1)) grid[0][Math.floor(rng() * cols)] = 1;
  if (!grid[rows - 1].some(c => c === 1)) grid[rows - 1][Math.floor(rng() * cols)] = 1;
  if (!grid.some(r => r[0] === 1)) grid[Math.floor(rng() * rows)][0] = 1;
  if (!grid.some(r => r[cols - 1] === 1)) grid[Math.floor(rng() * rows)][cols - 1] = 1;
  for (let k = 0; k < Math.max(2, Math.floor(rows * cols * 0.02)); k++) {
    const ri = Math.floor(rng() * Math.floor(rows / 2));
    const ci = Math.floor(rng() * Math.floor(cols / 2));
    grid[ri][ci] = grid[ri][ci] ? 0 : 1;
  }
  if (!grid[0].some(c => c === 1)) grid[0][0] = 1;
  if (!grid[rows - 1].some(c => c === 1)) grid[rows - 1][cols - 1] = 1;
  if (!grid.some(r => r[0] === 1)) grid[0][0] = 1;
  if (!grid.some(r => r[cols - 1] === 1)) grid[0][cols - 1] = 1;
  return grid.map(r => r.join(""));
}

// ─── Solver / Verifier ───
function isUniquelySolvable(solutionStrings) {
  const grid = solutionStrings.map(r => [...r].map(Number));
  const rows = grid.length, cols = grid[0].length;
  const UNKNOWN = -1, FILLED = 1, EMPTY = 0;
  function lineClues(line) {
    const c = []; let run = 0;
    for (const v of line) { if (v === 1) run++; else { if (run) c.push(run); run = 0; } }
    if (run) c.push(run); return c.length ? c : [0];
  }
  const rowClues = grid.map(r => lineClues(r));
  const colClues = Array.from({ length: cols }, (_, j) => lineClues(grid.map(r => r[j])));
  const work = Array.from({ length: rows }, () => Array(cols).fill(UNKNOWN));
  const CAP = 10000;
  function genArr(clues, length) {
    if (clues.length === 1 && clues[0] === 0) return [Array(length).fill(EMPTY)];
    const res = [];
    const minLen = clues.reduce((a, b) => a + b, 0) + clues.length - 1;
    if (minLen > length) return res;
    function place(ci, pos, cur) {
      if (res.length >= CAP) return;
      if (ci === clues.length) { while (cur.length < length) cur.push(EMPTY); res.push(cur); return; }
      const rem = clues.slice(ci).reduce((a, b) => a + b, 0) + (clues.length - ci - 1);
      for (let s = pos; s <= length - rem; s++) {
        if (res.length >= CAP) return;
        const next = [...cur];
        while (next.length < s) next.push(EMPTY);
        for (let i = 0; i < clues[ci]; i++) next.push(FILLED);
        if (ci < clues.length - 1) next.push(EMPTY);
        place(ci + 1, next.length, next);
      }
    }
    place(0, 0, []); return res;
  }
  function filterArr(arrs, known) { return arrs.filter(a => a.every((c, i) => known[i] === UNKNOWN || known[i] === c)); }
  function intersect(arrs, len) {
    const r = Array(len).fill(UNKNOWN); if (!arrs.length) return r;
    for (let i = 0; i < len; i++) {
      if (arrs.every(a => a[i] === FILLED)) r[i] = FILLED;
      else if (arrs.every(a => a[i] === EMPTY)) r[i] = EMPTY;
    }
    return r;
  }
  let rowArr = rowClues.map(c => genArr(c, cols));
  let colArr = colClues.map(c => genArr(c, rows));
  if (rowArr.some(a => !a.length || a.length >= CAP) || colArr.some(a => !a.length || a.length >= CAP)) return false;
  let changed = true, iter = 0;
  while (changed && iter < rows + cols + 50) {
    changed = false; iter++;
    for (let i = 0; i < rows; i++) {
      const f = filterArr(rowArr[i], work[i]); if (!f.length) return false; rowArr[i] = f;
      const r = intersect(f, cols);
      for (let j = 0; j < cols; j++) if (work[i][j] === UNKNOWN && r[j] !== UNKNOWN) { work[i][j] = r[j]; changed = true; }
    }
    for (let j = 0; j < cols; j++) {
      const known = work.map(r => r[j]);
      const f = filterArr(colArr[j], known); if (!f.length) return false; colArr[j] = f;
      const r = intersect(f, rows);
      for (let i = 0; i < rows; i++) if (work[i][j] === UNKNOWN && r[i] !== UNKNOWN) { work[i][j] = r[i]; changed = true; }
    }
  }
  return work.every(row => row.every(cell => cell !== UNKNOWN));
}

function generateVerifiedPuzzle(rows, cols, baseSeed) {
  for (let a = 0; a < 50; a++) {
    const sol = generatePuzzle(rows, cols, baseSeed + a);
    if (isUniquelySolvable(sol)) return sol;
  }
  return generatePuzzle(rows, cols, baseSeed);
}

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_CONFIG = [
  { rows: 5, cols: 5 }, { rows: 5, cols: 10 }, { rows: 10, cols: 10 },
  { rows: 10, cols: 15 }, { rows: 15, cols: 15 }, { rows: 15, cols: 20 }, { rows: 20, cols: 20 },
];

function dateToKey(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function getDayIdx(d) { const js = d.getDay(); return js === 0 ? 6 : js - 1; }
function formatDateShort(d) {
  const m = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${m[d.getMonth()]} ${d.getDate()}`;
}
function getPuzzleForDate(d) {
  const dayIdx = getDayIdx(d);
  const config = DAY_CONFIG[dayIdx];
  const baseSeed = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
  return { dayLabel: DAY_NAMES[dayIdx], rows: config.rows, cols: config.cols, solution: generateVerifiedPuzzle(config.rows, config.cols, baseSeed), dateKey: dateToKey(d), dateShort: formatDateShort(d) };
}
function getArchiveDates() {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(today); d.setDate(d.getDate() - i); return d; });
}
function deriveClues(solution) {
  const grid = solution.map(r => [...r].map(Number));
  const rows = grid.length, cols = grid[0].length;
  function lc(line) { const c = []; let run = 0; for (const v of line) { if (v === 1) run++; else { if (run) c.push(run); run = 0; } } if (run) c.push(run); return c.length ? c : [0]; }
  return { rowClues: grid.map(r => lc(r)), colClues: Array.from({ length: cols }, (_, j) => lc(grid.map(r => r[j]))), grid };
}
function formatTime(ms) { const s = Math.floor(ms / 1000); const m = Math.floor(s / 60); return m > 0 ? `${m}:${String(s % 60).padStart(2, "0")}` : `${s}s`; }
function getCellSize(rows, cols, embed) {
  const m = Math.max(rows, cols);
  if (embed) {
    if (m <= 5) return 56; if (m <= 10) return 44; if (m <= 15) return 36; return 30;
  }
  if (m <= 5) return 44; if (m <= 10) return 34; if (m <= 15) return 28; return 24;
}

// ─── Embed mode detection ───
function getIsEmbed() {
  try { return new URLSearchParams(window.location.search).get("embed") === "true"; } catch { return false; }
}

// ─── Streak & weekly helpers (localStorage-based) ───
function getStreakData() {
  try { const d = JSON.parse(localStorage.getItem("cleverland-streak") || "{}"); return d; } catch { return {}; }
}
function saveStreakData(data) {
  try { localStorage.setItem("cleverland-streak", JSON.stringify(data)); } catch {}
}
function computeStreak() {
  const data = getStreakData();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let streak = 0;
  for (let i = 0; i < 365; i++) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    if (data[dateToKey(d)]) streak++;
    else break;
  }
  return streak;
}
function computeWeeklyCount() {
  const data = getStreakData();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dayIdx = getDayIdx(today);
  let count = 0;
  for (let i = 0; i <= dayIdx; i++) {
    const d = new Date(today); d.setDate(d.getDate() - (dayIdx - i));
    if (data[dateToKey(d)]) count++;
  }
  return { count, total: dayIdx + 1 };
}
function markDaySolved(dateKey) {
  const data = getStreakData();
  data[dateKey] = true;
  saveStreakData(data);
}

// ═══════════════════════════════════════
//  CONFETTI
// ═══════════════════════════════════════
function Confetti({ active }) {
  const canvasRef = useRef(null); const animRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight;
    const palette = ["#2d5a27", "#6a9a5b", "#b8ccb1", "#d4a843", "#e8c86a", "#8b5e3c", "#c1724c"];
    const ps = Array.from({ length: 90 }, () => ({
      x: Math.random() * canvas.width, y: -20 - Math.random() * 200,
      w: 4 + Math.random() * 6, h: 4 + Math.random() * 6,
      vx: (Math.random() - 0.5) * 3, vy: 1.5 + Math.random() * 3,
      rot: Math.random() * Math.PI * 2, rotV: (Math.random() - 0.5) * 0.15,
      color: palette[Math.floor(Math.random() * palette.length)], opacity: 1,
    }));
    let frame = 0; const max = 180;
    const anim = () => {
      frame++; ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of ps) { p.x += p.vx; p.y += p.vy; p.vy += 0.04; p.rot += p.rotV; if (frame > max - 40) p.opacity = Math.max(0, p.opacity - 0.025); ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot); ctx.globalAlpha = p.opacity; ctx.fillStyle = p.color; ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h); ctx.restore(); }
      if (frame < max) animRef.current = requestAnimationFrame(anim);
    };
    animRef.current = requestAnimationFrame(anim);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [active]);
  if (!active) return null;
  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 9999 }} />;
}

// ═══════════════════════════════════════
//  STORAGE (localStorage for personal data)
// ═══════════════════════════════════════
const PFX = "cleverland-nono-";
function loadProgress(dateKey) { try { const r = localStorage.getItem(`${PFX}${dateKey}`); return r ? JSON.parse(r) : null; } catch { return null; } }
function saveProgress(dateKey, data) { try { localStorage.setItem(`${PFX}${dateKey}`, JSON.stringify(data)); } catch {} }
function deleteProgress(dateKey) { try { localStorage.removeItem(`${PFX}${dateKey}`); } catch {} }
// Community data uses Firebase — imported from ./firebase.js

// ═══════════════════════════════════════
//  STYLES
// ═══════════════════════════════════════
const fonts = { display: "'Libre Baskerville','Georgia',serif", mono: "'DM Mono','Courier New',monospace", body: "'Source Serif 4','Georgia',serif" };
const C = {
  bg: "#f7f9f4", card: "#ffffff", green1: "#1e4a1a", green2: "#2d5a27", green3: "#4a7a42", green4: "#6a9a5b",
  green5: "#8aaa7e", green6: "#b8ccb1", green7: "#d4e4ce", green8: "#eaf2e6",
  accent: "#c1894a", text: "#2a3926", textMuted: "#5a7d50",
  error: "#8b3a2a", errorBg: "#fdf0ed", filled: "#1e4a1a", cellBg: "#fafcf8",
  cellBorder: "#a3bca0", majorBorder: "#5a7d50", hoverClue: "#3a6e32", hoverClueBg: "#ddeeda",
};

// ═══════════════════════════════════════
//  SMALL COMPONENTS
// ═══════════════════════════════════════
function ToolBtn({ onClick, disabled, children, title, danger }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, fontFamily: fonts.display, fontSize: 12, fontWeight: 400, padding: "6px 12px", background: "transparent", color: disabled ? C.green6 : danger ? C.error : C.green3, border: `1.5px solid ${disabled ? C.green7 : danger ? "#e0b0a0" : C.green6}`, borderRadius: 7, cursor: disabled ? "default" : "pointer", transition: "all 0.2s", opacity: disabled ? 0.5 : 1 }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = danger ? C.errorBg : C.green8; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
    >{children}</button>
  );
}
const IconUndo = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>;
const IconRedo = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" /></svg>;
const IconPause = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>;
const IconPlay = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21" /></svg>;

// ─── Mini share for individual slides ───
function SlideShareBtn({ message }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(message); } catch { const t = document.createElement("textarea"); t.value = message; t.style.position = "fixed"; t.style.opacity = "0"; document.body.appendChild(t); t.select(); document.execCommand("copy"); document.body.removeChild(t); }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  }
  if (typeof navigator !== "undefined" && navigator.share) {
    return <button onClick={() => navigator.share({ title: "Daily Abstraction", text: message }).catch(() => {})} style={{ fontFamily: fonts.mono, fontSize: 11, padding: "5px 14px", background: C.green2, color: "#f5f9f3", border: "none", borderRadius: 6, cursor: "pointer", transition: "all 0.2s" }}>Share</button>;
  }
  return <button onClick={copy} style={{ fontFamily: fonts.mono, fontSize: 11, padding: "5px 14px", background: copied ? C.green3 : C.green8, color: copied ? "#fff" : C.green2, border: `1px solid ${copied ? C.green3 : C.green6}`, borderRadius: 6, cursor: "pointer", transition: "all 0.2s" }}>{copied ? "Copied!" : "Copy"}</button>;
}

// ─── Full share buttons row (used on <10 solvers screen) ───
function ShareButtons({ isTimed, elapsedMs, dayLabel, gridSize, url }) {
  const [copied, setCopied] = useState(false);
  const timeStr = isTimed ? formatTime(elapsedMs) : null;
  const message = isTimed
    ? `I solved the ${dayLabel} Daily Abstraction (${gridSize}) in ${timeStr}! Can you beat my time? 🧩\n${url}`
    : `I solved the ${dayLabel} Daily Abstraction (${gridSize})! Think you can solve it? 🧩\n${url}`;
  const enc = encodeURIComponent(message);
  async function copyMsg() {
    try { await navigator.clipboard.writeText(message); } catch { const t = document.createElement("textarea"); t.value = message; t.style.position = "fixed"; t.style.opacity = "0"; document.body.appendChild(t); t.select(); document.execCommand("copy"); document.body.removeChild(t); }
    setCopied(true); setTimeout(() => setCopied(false), 2500);
  }
  const bs = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: fonts.display, fontSize: 13, fontWeight: 500, padding: "9px 16px", borderRadius: 8, cursor: "pointer", transition: "all 0.2s", border: "none" };
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
        <a href={`https://wa.me/?text=${enc}`} target="_blank" rel="noopener noreferrer" style={{ ...bs, background: "#25D366", color: "#fff", textDecoration: "none" }}>WhatsApp</a>
        <a href={`sms:?&body=${enc}`} style={{ ...bs, background: "#34C759", color: "#fff", textDecoration: "none" }}>Text</a>
        {typeof navigator !== "undefined" && navigator.share ? (
          <button onClick={() => navigator.share({ title: "Daily Abstraction", text: message }).catch(() => {})} style={{ ...bs, background: C.green2, color: "#f5f9f3" }}>Share</button>
        ) : (
          <a href={`mailto:?subject=${encodeURIComponent("Can you beat my time? — Daily Abstraction")}&body=${enc}`} style={{ ...bs, background: C.green2, color: "#f5f9f3", textDecoration: "none" }}>Email</a>
        )}
        <button onClick={copyMsg} style={{ ...bs, background: copied ? C.green3 : C.green8, color: copied ? "#fff" : C.green2, border: `1.5px solid ${copied ? C.green3 : C.green6}` }}>{copied ? "Copied!" : "Copy"}</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
//  STATS SLIDESHOW
// ═══════════════════════════════════════
function StatsSlideshow({ slides, dayLabel, gridSize, url, onClose, noticeMessage }) {
  const [idx, setIdx] = useState(0);
  const total = slides.length + 1; // +1 for summary
  const isLast = idx === total - 1;
  const isSummary = idx >= slides.length;

  function next() { if (idx < total - 1) setIdx(idx + 1); }
  function prev() { if (idx > 0) setIdx(idx - 1); }

  // Summary message
  const summaryLines = slides.map(s => s.summaryLine).filter(Boolean);
  const summaryMsg = `Daily Abstraction — ${dayLabel} (${gridSize})\n${summaryLines.join("\n")}\n\nCan you beat me? 🧩\n${url}`;

  const slideStyle = {
    background: C.card, borderRadius: 14, padding: "32px 28px",
    minHeight: 220, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", textAlign: "center",
    position: "relative", border: `2px solid ${C.green7}`,
    boxShadow: "0 4px 24px rgba(45,90,39,0.08)",
    animation: "fadeInUp 0.3s ease both",
  };

  return (
    <div style={{ maxWidth: 400, margin: "0 auto" }}>
      {/* Dots */}
      <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 14 }}>
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} onClick={() => setIdx(i)} style={{
            width: i === idx ? 20 : 8, height: 8, borderRadius: 4,
            background: i === idx ? C.green2 : C.green6,
            cursor: "pointer", transition: "all 0.25s",
          }} />
        ))}
      </div>

      {/* Notice message */}
      {noticeMessage && (
        <div style={{ background: C.green8, borderRadius: 8, padding: "10px 16px", marginBottom: 12, fontSize: 12, color: C.green3, lineHeight: 1.5, textAlign: "center" }}>
          {noticeMessage}
        </div>
      )}

      {/* Slide */}
      <div key={idx} style={slideStyle}>
        {!isSummary ? (
          <>
            <div style={{ fontSize: 36, marginBottom: 12 }}>{slides[idx].emoji}</div>
            <div style={{ fontFamily: fonts.display, fontSize: 18, fontWeight: 700, color: C.green1, marginBottom: 8, lineHeight: 1.4 }}>
              {slides[idx].headline}
            </div>
            <p style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.6, margin: "0 0 16px", maxWidth: 300 }}>
              {slides[idx].body}
            </p>
            <SlideShareBtn message={`${slides[idx].shareText}\n\n${url}`} />
          </>
        ) : (
          <>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
            <div style={{ fontFamily: fonts.display, fontSize: 18, fontWeight: 700, color: C.green1, marginBottom: 12 }}>
              Your Summary
            </div>
            <div style={{ textAlign: "left", width: "100%", marginBottom: 16 }}>
              {slides.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: i < slides.length - 1 ? `1px solid ${C.green7}` : "none" }}>
                  <span style={{ fontSize: 20 }}>{s.emoji}</span>
                  <span style={{ fontSize: 13, color: C.green1, flex: 1 }}>{s.summaryLine}</span>
                </div>
              ))}
            </div>
            <SlideShareBtn message={summaryMsg} />
          </>
        )}
      </div>

      {/* Nav */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
        <button onClick={prev} disabled={idx === 0}
          style={{ fontFamily: fonts.display, fontSize: 13, padding: "8px 18px", background: "transparent", color: idx === 0 ? C.green6 : C.green3, border: `1.5px solid ${idx === 0 ? C.green7 : C.green6}`, borderRadius: 8, cursor: idx === 0 ? "default" : "pointer", transition: "all 0.2s", opacity: idx === 0 ? 0.4 : 1 }}>
          ← Prev
        </button>
        <span style={{ fontFamily: fonts.mono, fontSize: 11, color: C.green5 }}>{idx + 1} / {total}</span>
        {!isLast ? (
          <button onClick={next}
            style={{ fontFamily: fonts.display, fontSize: 13, padding: "8px 18px", background: C.green2, color: "#f5f9f3", border: `1.5px solid ${C.green2}`, borderRadius: 8, cursor: "pointer", transition: "all 0.2s" }}>
            Next →
          </button>
        ) : (
          <button onClick={onClose}
            style={{ fontFamily: fonts.display, fontSize: 13, padding: "8px 18px", background: C.green2, color: "#f5f9f3", border: `1.5px solid ${C.green2}`, borderRadius: 8, cursor: "pointer", transition: "all 0.2s" }}>
            Done
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
//  MAIN APP
// ═══════════════════════════════════════
export default function NonogramGame() {
  const embed = getIsEmbed();
  const [activeDateKey, setActiveDateKey] = useState(() => dateToKey(new Date()));
  const [puzzleInfo, setPuzzleInfo] = useState(null);
  const [puzzleData, setPuzzleData] = useState(null);
  const [playerGrid, setPlayerGrid] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hoverCell, setHoverCell] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragMode, setDragMode] = useState(null);
  const [archiveStatuses, setArchiveStatuses] = useState({});

  const [phase, setPhase] = useState("entry"); // "entry" | "playing" | "done"
  const [isTimed, setIsTimed] = useState(false);
  const [showTimer, setShowTimer] = useState(true);
  const [timerPaused, setTimerPaused] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const timerStartRef = useRef(null);
  const timerAccumulatedRef = useRef(0);
  const timerIntervalRef = useRef(null);

  const [firstCell, setFirstCell] = useState(null);
  const [communityStats, setCommunityStats] = useState(null);
  const [showStats, setShowStats] = useState(false);

  // Streak/weekly
  const [streak, setStreak] = useState(0);
  const [weeklyData, setWeeklyData] = useState({ count: 0, total: 7 });

  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const isDraggingRef = useRef(false);
  const dragModeRef = useRef(null);
  const playerGridRef = useRef(null);
  const completedRef = useRef(false);
  const dragStartGridRef = useRef(null);
  const completionRef = useRef(null);

  useEffect(() => { playerGridRef.current = playerGrid; }, [playerGrid]);
  useEffect(() => { completedRef.current = completed; }, [completed]);

  const todayKey = dateToKey(new Date());
  const isToday = activeDateKey === todayKey;

  // Timer
  function startTimer() { timerAccumulatedRef.current = 0; timerStartRef.current = Date.now(); setElapsedMs(0); timerIntervalRef.current = setInterval(() => { if (timerStartRef.current) setElapsedMs(timerAccumulatedRef.current + (Date.now() - timerStartRef.current)); }, 100); }
  function pauseTimer() { if (timerStartRef.current) { timerAccumulatedRef.current += Date.now() - timerStartRef.current; timerStartRef.current = null; } setTimerPaused(true); }
  function resumeTimer() { timerStartRef.current = Date.now(); setTimerPaused(false); }
  function stopTimer() { if (timerStartRef.current) { timerAccumulatedRef.current += Date.now() - timerStartRef.current; timerStartRef.current = null; } if (timerIntervalRef.current) clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; const f = timerAccumulatedRef.current; setElapsedMs(f); return f; }
  useEffect(() => () => { if (timerIntervalRef.current) clearInterval(timerIntervalRef.current); }, []);

  // Load puzzle
  const loadPuzzle = useCallback(async (dateKey) => {
    try {
      setLoading(true); setShowCelebration(false); setHoverCell(null); setUndoStack([]); setRedoStack([]); setFirstCell(null); setCommunityStats(null); setElapsedMs(0); setCompleted(false); setTimerPaused(false); setShowStats(false);
      timerAccumulatedRef.current = 0; timerStartRef.current = null;
      if (timerIntervalRef.current) { clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; }

      const [y, m, d] = dateKey.split("-").map(Number);
      const date = new Date(y, m - 1, d);
      const info = getPuzzleForDate(date);
      const clues = deriveClues(info.solution);
      setPuzzleInfo(info); setPuzzleData(clues);

      let saved = null;
      try { saved = await loadProgress(dateKey); } catch {}
      const gridIsValid = saved?.grid && Array.isArray(saved.grid) && saved.grid.length === info.rows && saved.grid.every(row => Array.isArray(row) && row.length === info.cols);
      const hasInteracted = gridIsValid && saved.grid.some(row => row.some(cell => cell !== 0));

      if (saved?.completed && gridIsValid) {
        setPlayerGrid(saved.grid); setCompleted(true); setPhase("done");
        setFirstCell(saved.firstCell || null); setIsTimed(saved.isTimed || false); setElapsedMs(saved.solveTime || 0);
        try { const comm = await loadCommunityData(dateKey); setCommunityStats(comm); } catch {}
      } else if (hasInteracted) {
        setPlayerGrid(saved.grid); setCompleted(false); setFirstCell(saved.firstCell || null); setIsTimed(saved.isTimed || false); setShowTimer(saved.showTimer ?? true);
        setElapsedMs(saved.elapsedMs || 0); timerAccumulatedRef.current = saved.elapsedMs || 0; setPhase("playing");
        if (saved.isTimed) { timerStartRef.current = Date.now(); timerIntervalRef.current = setInterval(() => { if (timerStartRef.current) setElapsedMs(timerAccumulatedRef.current + (Date.now() - timerStartRef.current)); }, 100); }
      } else {
        setPlayerGrid(Array.from({ length: info.rows }, () => Array(info.cols).fill(0)));
        setCompleted(false); setPhase("entry");
      }
      setActiveDateKey(dateKey);
      // Update streak/weekly
      setStreak(computeStreak()); setWeeklyData(computeWeeklyCount());
    } catch (err) {
      console.error("Error loading puzzle:", err);
      const [y, m, d] = dateKey.split("-").map(Number);
      const date = new Date(y, m - 1, d);
      const info = getPuzzleForDate(date); const clues = deriveClues(info.solution);
      setPuzzleInfo(info); setPuzzleData(clues);
      setPlayerGrid(Array.from({ length: info.rows }, () => Array(info.cols).fill(0)));
      setCompleted(false); setPhase("entry"); setActiveDateKey(dateKey);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    (async () => {
      try { const saved = await loadProgress(todayKey); if (saved && !saved.completed && saved.grid && Array.isArray(saved.grid) && !saved.grid.some(row => Array.isArray(row) && row.some(cell => cell !== 0))) await deleteProgress(todayKey); } catch {}
      loadPuzzle(todayKey);
    })();
  }, []);

  // Archive statuses
  useEffect(() => {
    async function ls() {
      const dates = getArchiveDates(); const st = {};
      for (const d of dates) { const key = dateToKey(d); try { const saved = await loadProgress(key); if (saved?.completed) st[key] = "solved"; else if (saved?.grid && Array.isArray(saved.grid) && saved.grid.some(row => Array.isArray(row) && row.some(cell => cell !== 0))) st[key] = "in-progress"; else st[key] = "new"; } catch { st[key] = "new"; } }
      setArchiveStatuses(st);
    }
    ls();
  }, [activeDateKey, completed]);

  // Save progress
  useEffect(() => {
    if (!playerGrid || phase === "entry") return;
    const hasInteracted = firstCell || completed;
    if (!hasInteracted) return;
    saveProgress(activeDateKey, { grid: playerGrid, completed, firstCell, isTimed, showTimer, elapsedMs: isTimed ? elapsedMs : 0, solveTime: completed && isTimed ? elapsedMs : undefined });
  }, [playerGrid, completed, activeDateKey, elapsedMs]);

  // Check completion
  useEffect(() => {
    if (!playerGrid || !puzzleData || completed || phase !== "playing") return;
    if (playerGrid.length !== puzzleData.grid.length) return;
    if (playerGrid[0]?.length !== puzzleData.grid[0]?.length) return;
    const correct = playerGrid.every((row, i) => row.every((cell, j) => { const sf = puzzleData.grid[i][j] === 1; return sf ? cell === 1 : cell !== 1; }));
    if (correct) {
      const solveTime = isTimed ? stopTimer() : 0;
      setCompleted(true); setShowCelebration(true); setPhase("done");
      saveProgress(activeDateKey, { grid: playerGrid, completed: true, firstCell, isTimed, showTimer, solveTime, elapsedMs: solveTime });
      // Mark streak
      markDaySolved(activeDateKey); setStreak(computeStreak()); setWeeklyData(computeWeeklyCount());
      // Community
      (async () => {
        const solveEntry = { time: isTimed ? solveTime : 0 };
        if (firstCell) { solveEntry.firstRow = firstCell.row; solveEntry.firstCol = firstCell.col; }
        await addCommunitysolve(activeDateKey, solveEntry);
        const comm = await loadCommunityData(activeDateKey);
        setCommunityStats(comm);
      })();
      setTimeout(() => setShowCelebration(false), 4000);
      setTimeout(() => completionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 400);
    }
  }, [playerGrid]);

  // Track community starts
  function startPlaying(timed, visible) {
    setIsTimed(timed); setShowTimer(visible); setPhase("playing");
    if (timed) startTimer();
    incrementCommunityStarts(activeDateKey);
  }

  // Undo/redo
  function undo() { if (undoStack.length === 0 || completedRef.current) return; const p = undoStack[undoStack.length - 1]; setUndoStack(s => s.slice(0, -1)); setRedoStack(s => [...s, playerGridRef.current]); setPlayerGrid(p); }
  function redo() { if (redoStack.length === 0 || completedRef.current) return; const n = redoStack[redoStack.length - 1]; setRedoStack(s => s.slice(0, -1)); setUndoStack(s => [...s, playerGridRef.current]); setPlayerGrid(n); }
  function restart() { if (completedRef.current || !puzzleInfo) return; setUndoStack(prev => [...prev, playerGridRef.current]); setRedoStack([]); setPlayerGrid(Array.from({ length: puzzleInfo.rows }, () => Array(puzzleInfo.cols).fill(0))); setFirstCell(null); if (isTimed) { stopTimer(); startTimer(); } }

  // Drag
  function handleCellPointerDown(i, j, e) {
    if (completedRef.current) return; e.preventDefault();
    const grid = playerGridRef.current; if (!grid) return;
    const nv = (grid[i][j] + 1) % 3;
    dragStartGridRef.current = grid; isDraggingRef.current = true; dragModeRef.current = nv;
    setIsDragging(true); setDragMode(nv);
    const ng = grid.map(r => [...r]); ng[i][j] = nv; setPlayerGrid(ng);
    if (!firstCell) setFirstCell({ row: i, col: j });
  }
  function handleCellPointerEnter(i, j) {
    setHoverCell({ row: i, col: j });
    if (isDraggingRef.current && dragModeRef.current !== null && !completedRef.current) {
      const grid = playerGridRef.current; if (!grid) return;
      const ng = grid.map(r => [...r]); ng[i][j] = dragModeRef.current; setPlayerGrid(ng);
    }
  }
  const handlePointerUp = useCallback(() => {
    if (isDraggingRef.current && dragStartGridRef.current) {
      const snap = dragStartGridRef.current;
      setUndoStack(prev => { const n = [...prev, snap]; if (n.length > 80) n.shift(); return n; });
      setRedoStack([]); dragStartGridRef.current = null;
    }
    isDraggingRef.current = false; dragModeRef.current = null; setIsDragging(false); setDragMode(null);
  }, []);
  useEffect(() => { window.addEventListener("pointerup", handlePointerUp); return () => window.removeEventListener("pointerup", handlePointerUp); }, [handlePointerUp]);

  // ─── Build stats slides ───
  function buildSlides() {
    const slides = [];
    const allSolves = communityStats?.solves || [];
    const times = allSolves.filter(s => s.time > 0).map(s => s.time);
    const totalSolves = allSolves.length;
    const totalStarts = communityStats?.starts || 0;

    // 0. First row / first column match (community)
    if (firstCell && totalSolves >= 2) {
      const solvesWithFirst = allSolves.filter(s => s.firstRow !== undefined && s.firstCol !== undefined);
      if (solvesWithFirst.length >= 2) {
        const rowMatches = solvesWithFirst.filter(s => s.firstRow === firstCell.row).length;
        const colMatches = solvesWithFirst.filter(s => s.firstCol === firstCell.col).length;
        const rowPct = Math.round((rowMatches / solvesWithFirst.length) * 100);
        const colPct = Math.round((colMatches / solvesWithFirst.length) * 100);
        slides.push({
          emoji: "🎯", isLocal: false,
          headline: "Your starting move",
          body: `${rowPct}% of solvers started in the same row as you, and ${colPct}% started in the same column.`,
          shareText: `${rowPct}% of solvers started in my row and ${colPct}% in my column on today's Daily Abstraction! 🎯🧩`,
          summaryLine: `🎯 ${rowPct}% same row · ${colPct}% same column`,
        });
      }
    }

    // 1. Percentile (timed, community)
    if (isTimed && elapsedMs > 0 && times.length >= 2) {
      const fasterCount = times.filter(t => t > elapsedMs).length;
      const pct = Math.round((fasterCount / times.length) * 100);
      slides.push({
        emoji: "⚡", isLocal: false, headline: `Faster than ${pct}% of solvers`,
        body: `You solved in ${formatTime(elapsedMs)}. ${times.length} people have solved timed today.`,
        shareText: `I solved the ${puzzleInfo.dayLabel} Daily Abstraction (${puzzleInfo.rows}×${puzzleInfo.cols}) faster than ${pct}% of solvers! Can you beat ${formatTime(elapsedMs)}? 🧩`,
        summaryLine: `⚡ Faster than ${pct}% of solvers (${formatTime(elapsedMs)})`,
      });
    }

    // 2. Median solve time (timed, community)
    if (isTimed && times.length >= 3) {
      const sorted = [...times].sort((a, b) => a - b);
      const median = sorted[Math.floor(sorted.length / 2)];
      const faster = elapsedMs < median;
      slides.push({
        emoji: "⏱", isLocal: false, headline: `Median time: ${formatTime(median)}`,
        body: faster ? `You beat the median by ${formatTime(median - elapsedMs)}!` : `The median solver was ${formatTime(elapsedMs - median)} faster.`,
        shareText: `Today's Daily Abstraction median: ${formatTime(median)}. I got ${formatTime(elapsedMs)}. Can you beat it? 🧩`,
        summaryLine: `⏱ Median: ${formatTime(median)} · You: ${formatTime(elapsedMs)}`,
      });
    }

    // 3. Completion rate (community)
    if (totalStarts > 2 && totalSolves > 0) {
      const rate = Math.round((totalSolves / totalStarts) * 100);
      slides.push({
        emoji: "🏁", isLocal: false, headline: `${rate}% completion rate`,
        body: `${totalSolves} out of ${totalStarts} people who started today's puzzle finished it. You're one of them!`,
        shareText: `Only ${rate}% of people finished today's Daily Abstraction. I did! 🧩`,
        summaryLine: `🏁 ${rate}% of starters finished`,
      });
    }

    // 4. Day streak (local)
    if (streak > 0) {
      const fireEmojis = streak >= 7 ? "🔥🔥🔥" : streak >= 3 ? "🔥🔥" : "🔥";
      slides.push({
        emoji: fireEmojis, isLocal: true, headline: `${streak} day streak!`,
        body: streak === 1 ? "You've started a streak! Come back tomorrow to keep it going." : `You've solved ${streak} days in a row. Keep it going!`,
        shareText: `I'm on a ${streak}-day streak on Daily Abstraction! 🔥🧩`,
        summaryLine: `🔥 ${streak} day streak`,
      });
    }

    // 5. Weekly completion (local)
    const { count, total } = weeklyData;
    if (count > 0) {
      const allDone = count === total;
      slides.push({
        emoji: allDone ? "🌟" : "📅", isLocal: true, headline: allDone ? "Perfect week!" : `${count} of ${total} this week`,
        body: allDone ? "You've solved every puzzle this week. Incredible!" : `You've solved ${count} out of ${total} puzzles so far this week.`,
        shareText: allDone ? `Perfect week on Daily Abstraction — ${total}/${total} puzzles solved! 🌟🧩` : `I've solved ${count}/${total} Daily Abstraction puzzles this week! 🧩`,
        summaryLine: allDone ? `🌟 Perfect week: ${total}/${total}` : `📅 ${count}/${total} this week`,
      });
    }

    return slides;
  }

  const cellSize = puzzleInfo ? getCellSize(puzzleInfo.rows, puzzleInfo.cols, embed) : 28;

  return (
    <div style={{ fontFamily: fonts.body, background: C.bg, minHeight: "100vh", color: C.text, userSelect: "none", WebkitUserSelect: "none" }}
      onContextMenu={e => e.preventDefault()} onPointerLeave={() => setHoverCell(null)}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=DM+Mono:wght@400;500&family=Source+Serif+4:ital,wght@0,400;0,600;1,400&display=swap');
        * { box-sizing: border-box; }
        @keyframes fadeInUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes scaleIn { from { opacity:0; transform:scale(0.92); } to { opacity:1; transform:scale(1); } }
        @keyframes celebratePulse { 0%,100% { transform:scale(1); } 50% { transform:scale(1.03); } }
        .nono-cell { position:relative; transition:background 0.08s ease; touch-action:none; cursor:pointer; }
        .nono-cell:hover { outline:2px solid ${C.green4}; outline-offset:-2px; z-index:1; }
        .btn-primary:hover { background:${C.green3} !important; }
        .archive-btn { transition:all 0.2s; }
        .archive-btn:hover { border-color:${C.green4} !important; box-shadow:0 2px 12px rgba(45,90,39,0.1); }
        .entry-opt { transition:all 0.2s; cursor:pointer; }
        .entry-opt:hover { border-color:${C.green4} !important; box-shadow:0 4px 20px rgba(45,90,39,0.1); transform:translateY(-1px); }
      `}</style>

      <Confetti active={showCelebration} />

      <div style={{ maxWidth: embed ? 1100 : 860, margin: "0 auto", padding: embed ? "8px 12px 16px" : "24px 16px 48px" }}>
        {/* HEADER — hidden in embed mode */}
        {!embed && (
        <header style={{ textAlign: "center", padding: "8px 0 20px", borderBottom: `2px solid ${C.green7}`, marginBottom: 24 }}>
          <h1 style={{ fontFamily: fonts.display, fontSize: 26, fontWeight: 700, color: C.green2, margin: "0 0 4px", letterSpacing: "-0.3px" }}>Daily Abstraction</h1>
          <p style={{ fontFamily: fonts.mono, fontSize: 11, color: C.green5, textTransform: "uppercase", letterSpacing: 3, margin: 0 }}>cleverland puzzle design</p>
        </header>
        )}

        {/* ARCHIVE */}
        <div style={{ display: "flex", gap: embed ? 4 : 6, justifyContent: "center", flexWrap: "wrap", marginBottom: embed ? 10 : 20 }}>
          {getArchiveDates().map((d, i) => {
            const key = dateToKey(d); const dayIdx = getDayIdx(d); const isActive = key === activeDateKey; const isTodayDate = i === 0; const status = archiveStatuses[key] || "new";
            return (
              <button key={key} className="archive-btn"
                onClick={() => { if (timerIntervalRef.current) { if (timerStartRef.current) { timerAccumulatedRef.current += Date.now() - timerStartRef.current; timerStartRef.current = null; } clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; } loadPuzzle(key); }}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: embed ? "5px 8px" : "8px 10px", minWidth: embed ? 48 : 58, background: isActive ? C.green2 : C.card, color: isActive ? "#f5f9f3" : C.text, border: `1.5px solid ${isActive ? C.green2 : C.green7}`, borderRadius: embed ? 8 : 10, cursor: "pointer", fontFamily: fonts.mono, fontSize: embed ? 9 : 10, position: "relative" }}>
                <span style={{ fontWeight: 600, fontSize: embed ? 10 : 11 }}>{DAY_NAMES[dayIdx].slice(0, 3)}</span>
                <span style={{ fontSize: embed ? 8 : 9, opacity: isActive ? 0.8 : 0.6, marginTop: 1 }}>{formatDateShort(d)}</span>
                {status === "solved" && <span style={{ position: "absolute", top: -4, right: -4, width: 10, height: 10, borderRadius: "50%", background: isActive ? "#a3e89a" : C.green3, border: `2px solid ${C.bg}` }} />}
                {status === "in-progress" && <span style={{ position: "absolute", top: -4, right: -4, width: 10, height: 10, borderRadius: "50%", background: C.accent, border: `2px solid ${C.bg}` }} />}
                {isTodayDate && <span style={{ fontSize: 7, textTransform: "uppercase", letterSpacing: 1, color: isActive ? "#c8ebc5" : C.green4, marginTop: 1 }}>today</span>}
              </button>
            );
          })}
        </div>

        {loading && <p style={{ textAlign: "center", color: C.green5, marginTop: 40 }}>Loading puzzle…</p>}

        {/* ENTRY */}
        {!loading && phase === "entry" && puzzleInfo && (
          <div style={{ animation: "fadeInUp 0.5s ease both", textAlign: "center" }}>
            <div style={{ fontFamily: fonts.display, fontSize: 22, fontWeight: 700, color: C.green1, marginBottom: 4 }}>{puzzleInfo.dayLabel} Nonogram</div>
            <div style={{ fontFamily: fonts.mono, fontSize: 12, color: C.green5, marginBottom: 28 }}>{puzzleInfo.rows} × {puzzleInfo.cols}</div>
            <p style={{ fontSize: 15, lineHeight: 1.8, color: C.textMuted, maxWidth: 420, margin: "0 auto 28px" }}>Would you like to time your solve?</p>
            <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginBottom: 20 }}>
              <div className="entry-opt" onClick={() => startPlaying(true, true)} style={{ background: C.card, border: `2px solid ${C.green7}`, borderRadius: 14, padding: "24px 28px", flex: "1 1 160px", maxWidth: 220, textAlign: "center" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>⏱</div>
                <div style={{ fontFamily: fonts.display, fontSize: 16, fontWeight: 700, color: C.green1, marginBottom: 6 }}>Timed</div>
                <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.6 }}>Race the clock and see how you compare</div>
              </div>
              <div className="entry-opt" onClick={() => startPlaying(false, false)} style={{ background: C.card, border: `2px solid ${C.green7}`, borderRadius: 14, padding: "24px 28px", flex: "1 1 160px", maxWidth: 220, textAlign: "center" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🧩</div>
                <div style={{ fontFamily: fonts.display, fontSize: 16, fontWeight: 700, color: C.green1, marginBottom: 6 }}>Relaxed</div>
                <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.6 }}>Solve at your own pace</div>
              </div>
            </div>
            <details style={{ background: C.card, border: `1.5px solid ${C.green7}`, borderRadius: 12, padding: "0 20px", maxWidth: 460, margin: "16px auto 0" }}>
              <summary style={{ fontFamily: fonts.display, fontSize: 14, fontWeight: 700, color: C.green2, cursor: "pointer", padding: "14px 0", listStyle: "none" }}><span style={{ marginRight: 8 }}>?</span> How to Play</summary>
              <div style={{ fontSize: 13, lineHeight: 1.8, color: C.textMuted, paddingBottom: 16 }}>
                <p style={{ margin: "0 0 8px" }}>Each row and column has numbers showing how many groups of consecutive filled cells exist.</p>
                <p style={{ margin: "0 0 8px" }}><strong style={{ color: C.green2 }}>Click once</strong> to fill. <strong style={{ color: C.green2 }}>Again</strong> to mark unfilled (·). <strong style={{ color: C.green2 }}>Third time</strong> to clear.</p>
                <p style={{ margin: 0 }}>Hold and drag to apply across multiple cells.</p>
              </div>
            </details>
          </div>
        )}

        {/* PLAYING / DONE */}
        {!loading && (phase === "playing" || phase === "done") && playerGrid && puzzleInfo && puzzleData && (
          <div style={{ animation: "scaleIn 0.3s ease both" }}>
            <div style={{ textAlign: "center", marginBottom: embed ? 2 : 4 }}>
              <div style={{ fontFamily: fonts.display, fontSize: embed ? 18 : 22, fontWeight: 700, color: C.green1 }}>{puzzleInfo.dayLabel} Nonogram</div>
              <div style={{ fontFamily: fonts.mono, fontSize: embed ? 11 : 12, color: C.green5 }}>{puzzleInfo.rows} × {puzzleInfo.cols}{!isToday && <span style={{ marginLeft: 8, color: C.green4, fontStyle: "italic" }}>· {puzzleInfo.dateShort}</span>}</div>
            </div>

            {isTimed && phase === "playing" && showTimer && (
              <div style={{ textAlign: "center", marginBottom: 8 }}>
                <span style={{ fontFamily: fonts.mono, fontSize: 20, fontWeight: 500, color: timerPaused ? C.green5 : C.green2, letterSpacing: 1 }}>{formatTime(elapsedMs)}</span>
                {timerPaused && <span style={{ fontSize: 11, color: C.accent, marginLeft: 8, fontStyle: "italic" }}>paused</span>}
              </div>
            )}

            <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap", marginBottom: embed ? 8 : 14 }}>
              <ToolBtn onClick={undo} disabled={undoStack.length === 0 || completed} title="Undo"><IconUndo /> Undo</ToolBtn>
              <ToolBtn onClick={redo} disabled={redoStack.length === 0 || completed} title="Redo"><IconRedo /> Redo</ToolBtn>
              {isTimed && !completed && <ToolBtn onClick={() => { timerPaused ? resumeTimer() : pauseTimer(); }} title={timerPaused ? "Resume" : "Pause"}>{timerPaused ? <IconPlay /> : <IconPause />} {timerPaused ? "Resume" : "Pause"}</ToolBtn>}
              {isTimed && !completed && <ToolBtn onClick={() => setShowTimer(v => !v)} title={showTimer ? "Hide timer" : "Show timer"}>{showTimer ? "Hide" : "Show"} timer</ToolBtn>}
              <ToolBtn onClick={restart} disabled={completed} title="Restart puzzle" danger>Restart</ToolBtn>
            </div>

            {timerPaused && !completed && (
              <div style={{ textAlign: "center", padding: "40px 20px", margin: "0 auto 20px", background: C.card, border: `2px solid ${C.green7}`, borderRadius: 14, maxWidth: 400 }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>⏸</div>
                <div style={{ fontFamily: fonts.display, fontSize: 18, fontWeight: 700, color: C.green1, marginBottom: 8 }}>Paused</div>
                <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 16 }}>The grid is hidden while paused.</p>
                <button className="btn-primary" onClick={resumeTimer} style={{ fontFamily: fonts.display, fontSize: 14, fontWeight: 600, padding: "10px 24px", background: C.green2, color: "#f5f9f3", border: `2px solid ${C.green2}`, borderRadius: 8, cursor: "pointer" }}>Resume</button>
              </div>
            )}

            {(!timerPaused || completed) && (
              <div style={{ overflowX: "auto", display: "flex", justifyContent: "center", padding: "8px 0", touchAction: "none" }}>
                <GameGrid playerGrid={playerGrid} puzzleData={puzzleData} rows={puzzleInfo.rows} cols={puzzleInfo.cols} completed={completed} hoverCell={hoverCell} onCellPointerDown={handleCellPointerDown} onCellPointerEnter={handleCellPointerEnter} onMouseLeaveGrid={() => setHoverCell(null)} cellSize={cellSize} />
              </div>
            )}

            {!completed && !timerPaused && <p style={{ textAlign: "center", fontSize: 11, color: C.green5, fontFamily: fonts.mono, marginTop: 12, fontStyle: "italic" }}>click: fill → unfilled → empty · drag to continue</p>}

            {/* COMPLETION */}
            {completed && (
              <div ref={completionRef} style={{ textAlign: "center", marginTop: 28, animation: "fadeInUp 0.6s ease both", animationDelay: "0.2s" }}>
                {!showStats ? (
                  <div style={{ background: C.card, border: `2px solid ${C.green4}`, borderRadius: 16, padding: "28px 32px", maxWidth: 440, margin: "0 auto", boxShadow: "0 8px 32px rgba(45,90,39,0.12)" }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>✨</div>
                    <h2 style={{ fontFamily: fonts.display, fontSize: 22, fontWeight: 700, color: C.green1, margin: "0 0 12px" }}>Beautifully solved!</h2>
                    {isTimed && <div style={{ fontFamily: fonts.mono, fontSize: 22, fontWeight: 500, color: C.green2, marginBottom: 16, letterSpacing: 1 }}>{formatTime(elapsedMs)}</div>}
                    <p style={{ fontSize: 14, lineHeight: 1.7, color: C.textMuted, margin: "0 0 20px" }}>
                      {isToday ? "A new puzzle arrives tomorrow — try the past week's puzzles above!" : "Check the calendar above for more puzzles to try!"}
                    </p>
                    <button className="btn-primary" onClick={() => setShowStats(true)}
                      style={{ fontFamily: fonts.display, fontSize: 14, fontWeight: 600, padding: "11px 28px", background: C.green2, color: "#f5f9f3", border: `2px solid ${C.green2}`, borderRadius: 8, cursor: "pointer", transition: "all 0.2s", marginBottom: 0 }}>
                      View Stats
                    </button>
                  </div>
                ) : (() => {
                  const communityCount = communityStats?.solves?.length || 0;
                  const hasEnoughCommunity = communityCount >= 10;
                  const allSlides = buildSlides();
                  // Local-only slides (streak, weekly) always show; community slides need 10+
                  const localSlides = allSlides.filter(s => s.isLocal);
                  const communitySlides = allSlides.filter(s => !s.isLocal);
                  const visibleSlides = hasEnoughCommunity ? allSlides : localSlides;

                  if (visibleSlides.length === 0 && !hasEnoughCommunity) {
                    // No local stats and not enough community data
                    return (
                      <div style={{ background: C.card, border: `2px solid ${C.green7}`, borderRadius: 16, padding: "28px 32px", maxWidth: 440, margin: "0 auto", boxShadow: "0 4px 20px rgba(45,90,39,0.08)" }}>
                        <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
                        <h3 style={{ fontFamily: fonts.display, fontSize: 18, fontWeight: 700, color: C.green1, margin: "0 0 10px" }}>Stats are building up!</h3>
                        <p style={{ fontSize: 14, lineHeight: 1.7, color: C.textMuted, margin: "0 0 6px" }}>
                          Community stats unlock when at least 10 people have solved today's puzzle. So far {communityCount} {communityCount === 1 ? "person has" : "people have"} solved it.
                        </p>
                        <p style={{ fontSize: 14, lineHeight: 1.7, color: C.textMuted, margin: "0 0 20px" }}>
                          Share the puzzle with friends to unlock stats faster — and come back tomorrow for a new challenge!
                        </p>
                        <ShareButtons isTimed={isTimed} elapsedMs={elapsedMs} dayLabel={puzzleInfo.dayLabel} gridSize={`${puzzleInfo.rows}×${puzzleInfo.cols}`} url="https://cleverlandpuzzle.com" />
                        <button onClick={() => setShowStats(false)}
                          style={{ fontFamily: fonts.display, fontSize: 13, padding: "8px 20px", background: "transparent", color: C.green3, border: `1.5px solid ${C.green6}`, borderRadius: 8, cursor: "pointer", transition: "all 0.2s", marginTop: 8 }}>
                          ← Back
                        </button>
                      </div>
                    );
                  }

                  if (visibleSlides.length > 0 && !hasEnoughCommunity) {
                    // Has local stats but not enough community — show local slides + message
                    return (
                      <div>
                        <StatsSlideshow
                          slides={visibleSlides}
                          dayLabel={puzzleInfo.dayLabel}
                          gridSize={`${puzzleInfo.rows}×${puzzleInfo.cols}`}
                          url="https://cleverlandpuzzle.com"
                          onClose={() => setShowStats(false)}
                          noticeMessage={`Community stats unlock at 10 solvers (${communityCount} so far). Share to help unlock them!`}
                        />
                      </div>
                    );
                  }

                  // Full stats available
                  return (
                    <StatsSlideshow
                      slides={visibleSlides}
                      dayLabel={puzzleInfo.dayLabel}
                      gridSize={`${puzzleInfo.rows}×${puzzleInfo.cols}`}
                      url="https://cleverlandpuzzle.com"
                      onClose={() => setShowStats(false)}
                    />
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {!embed && (
        <footer style={{ marginTop: 56, paddingTop: 16, borderTop: `1px solid ${C.green7}`, textAlign: "center" }}>
          <p style={{ fontFamily: fonts.mono, fontSize: 10, color: C.green5, textTransform: "uppercase", letterSpacing: 2, margin: 0 }}>nonogram · cleverland puzzle design</p>
        </footer>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
//  GAME GRID
// ═══════════════════════════════════════
function GameGrid({ playerGrid, puzzleData, rows, cols, completed, hoverCell, onCellPointerDown, onCellPointerEnter, onMouseLeaveGrid, cellSize }) {
  const { rowClues, colClues } = puzzleData;
  const maxRL = Math.max(...rowClues.map(c => c.length));
  const maxCL = Math.max(...colClues.map(c => c.length));
  const clueW = cellSize > 30 ? 24 : cellSize > 26 ? 20 : 18;
  const clueH = cellSize > 30 ? 22 : 18;
  const cFS = cellSize > 30 ? 14 : cellSize > 26 ? 12 : 10;
  const tableRef = useRef(null);
  const lastTouchCellRef = useRef(null);

  function isSat(lc, cells) { const runs = []; let run = 0; for (const c of cells) { if (c === 1) run++; else { if (run) runs.push(run); run = 0; } } if (run) runs.push(run); if (lc.length === 1 && lc[0] === 0) return runs.length === 0; if (runs.length !== lc.length) return false; return runs.every((r, i) => r === lc[i]); }
  const rowSat = rowClues.map((cl, i) => isSat(cl, playerGrid[i]));
  const colSat = colClues.map((cl, j) => isSat(cl, playerGrid.map(r => r[j])));
  const hr = hoverCell?.row ?? -1, hc = hoverCell?.col ?? -1;

  function rS(i, ci) { const sat = rowSat[i]; const hov = i === hr && !completed; return { width: clueW, height: cellSize, textAlign: "right", paddingRight: ci === maxRL - 1 ? 8 : 4, paddingLeft: 2, fontSize: cFS, fontWeight: hov ? 700 : 500, color: sat ? C.green6 : hov ? C.hoverClue : C.green2, background: hov && !sat ? C.hoverClueBg : "transparent", borderRadius: 3, whiteSpace: "nowrap", transition: "color 0.15s,background 0.15s,font-weight 0.15s" }; }
  function cS(j, cr) { const sat = colSat[j]; const hov = j === hc && !completed; return { width: cellSize, maxWidth: cellSize, minWidth: cellSize, height: clueH, textAlign: "center", fontSize: cFS, fontWeight: hov ? 700 : 500, color: sat ? C.green6 : hov ? C.hoverClue : C.green2, background: hov && !sat ? C.hoverClueBg : "transparent", borderRadius: 3, verticalAlign: "bottom", paddingBottom: cr === maxCL - 1 ? 4 : 0, overflow: "hidden", transition: "color 0.15s,background 0.15s,font-weight 0.15s" }; }

  // Mobile drag: use pointerMove to find cell under finger
  function handlePointerMove(e) {
    if (e.pointerType !== "touch") return; // Desktop uses onPointerEnter which works fine
    const el = document.elementFromPoint(e.clientX, e.clientY);
    if (!el) return;
    const td = el.closest("td[data-cell]");
    if (!td) return;
    const r = parseInt(td.dataset.row, 10);
    const c = parseInt(td.dataset.col, 10);
    if (isNaN(r) || isNaN(c)) return;
    const key = `${r},${c}`;
    if (lastTouchCellRef.current === key) return; // avoid duplicate calls
    lastTouchCellRef.current = key;
    onCellPointerEnter(r, c);
  }

  function handlePointerUpLocal() {
    lastTouchCellRef.current = null;
  }

  return (
    <div style={{ display: "inline-block", animation: completed ? "celebratePulse 0.6s ease 2" : "none" }}
      onMouseLeave={onMouseLeaveGrid}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUpLocal}
    >
      <table ref={tableRef} style={{ borderCollapse: "collapse", fontFamily: fonts.mono, tableLayout: "fixed" }}>
        {/* colgroup to enforce fixed column widths */}
        <colgroup>
          {Array.from({ length: maxRL }).map((_, ci) => (
            <col key={`rc-${ci}`} style={{ width: clueW }} />
          ))}
          {Array.from({ length: cols }).map((_, j) => (
            <col key={`gc-${j}`} style={{ width: cellSize }} />
          ))}
        </colgroup>
        <thead>
          {Array.from({ length: maxCL }).map((_, cr) => (
            <tr key={`ch-${cr}`}>
              {Array.from({ length: maxRL }).map((_, ci) => (
                <td key={`blank-${ci}`} />
              ))}
              {colClues.map((clue, j) => { const off = maxCL - clue.length; const val = cr >= off ? clue[cr - off] : ""; return <td key={j} style={cS(j, cr)}>{val === 0 ? "–" : val}</td>; })}
            </tr>
          ))}
        </thead>
        <tbody>
          {playerGrid.map((row, i) => (
            <tr key={i}>
              {Array.from({ length: maxRL }).map((_, ci) => { const off = maxRL - rowClues[i].length; const val = ci >= off ? rowClues[i][ci - off] : ""; return <td key={`rc-${ci}`} style={rS(i, ci)}>{val === 0 ? "–" : val}</td>; })}
              {row.map((cell, j) => {
                const majR = (j + 1) % 5 === 0 && j < cols - 1; const majB = (i + 1) % 5 === 0 && i < rows - 1;
                return (
                  <td key={j} className="nono-cell"
                    data-cell="1" data-row={i} data-col={j}
                    onPointerDown={e => onCellPointerDown(i, j, e)}
                    onPointerEnter={() => onCellPointerEnter(i, j)}
                    style={{ width: cellSize, maxWidth: cellSize, minWidth: cellSize, height: cellSize, border: `1px solid ${C.cellBorder}`, borderRightWidth: majR ? 2.5 : 1, borderBottomWidth: majB ? 2.5 : 1, borderRightColor: majR ? C.majorBorder : C.cellBorder, borderBottomColor: majB ? C.majorBorder : C.cellBorder, background: cell === 1 ? (completed ? C.green2 : C.filled) : C.cellBg, textAlign: "center", verticalAlign: "middle", cursor: completed ? "default" : "pointer" }}>
                    {cell === 2 && <span style={{ display: "inline-block", width: Math.max(5, cellSize * 0.2), height: Math.max(5, cellSize * 0.2), borderRadius: "50%", background: C.green3 }} />}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
